import { KeyboardEvent, useLayoutEffect, useRef } from 'react';

import { getTargetIndex } from './utils/get-target-index';
import { ENABLED_CARD_SELECTOR } from './constants';
import { InputCardGroupProps } from './input-card-group.type';

/**
 * A radio group of `InputCard`s: one Tab stop, on the checked card or else the first one, and the arrows, Home and End
 * move to a card and check it, as `PillToggleGroup` does. The Tab stop is recomputed when the group renders, so each
 * card's `isChecked` must come from the component that renders the group.
 */
export const InputCardGroup = ({ ariaLabel, className, children }: InputCardGroupProps) => {
  const groupRef = useRef<HTMLDivElement>(null);

  const getCards = () => [...(groupRef.current?.querySelectorAll<HTMLButtonElement>(ENABLED_CARD_SELECTOR) ?? [])];

  // The cards don't set their own tabindex, so setting it here after every render doesn't fight React.
  useLayoutEffect(() => {
    const cards = getCards();
    const tabStop = cards.find((card) => card.getAttribute('aria-checked') === 'true') ?? cards[0];
    cards.forEach((card) => {
      card.tabIndex = card === tabStop ? 0 : -1;
    });
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Leaves browser shortcuts such as Alt+← (back) or Cmd+↓ (page end) to the browser.
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const cards = getCards();
    const current = event.target instanceof HTMLButtonElement ? cards.indexOf(event.target) : -1;
    if (current === -1) return;
    const target = getTargetIndex(event.key, current, cards.length);
    if (target === null) return;

    event.preventDefault();
    cards[target].focus();
    // Clicking a checked card again unchecks it in some lists, so a card already checked is only focused.
    if (cards[target].getAttribute('aria-checked') !== 'true') cards[target].click();
  };

  return (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- the keys come from the cards, which take the focus
    <div ref={groupRef} role='radiogroup' aria-label={ariaLabel} className={className} onKeyDown={handleKeyDown}>
      {children}
    </div>
  );
};
